import { useMemo } from "react";
import { useFavoritesStore, type FavoriteKind } from "./favoritesStore.js";
import { activeProfileId } from "../settings/profileStore.js";

/**
 * Shared favorites selectors (Section 11). Pages subscribe to the raw record
 * (never a function that returns a fresh array, which would re-render on every
 * store change) and derive here in a memo.
 */

/** Favorite content refs of one kind, newest favorite first. */
export function useFavoriteRefs(kind: FavoriteKind): string[] {
  const favorites = useFavoritesStore((s) => s.favorites);
  return useMemo(
    () =>
      Object.values(favorites)
        .filter((row) => row.profileId === activeProfileId() && row.contentType === kind)
        .sort((a, b) => b.addedAt.localeCompare(a.addedAt))
        .map((row) => row.contentRef),
    [favorites, kind],
  );
}

/** Channel ids the user has hidden — hidden channels stay out of the guide. */
export function useHiddenChannelIds(): Set<string> {
  const hidden = useFavoritesStore((s) => s.hidden);
  return useMemo(
    () =>
      new Set(
        Object.values(hidden)
          .filter((row) => row.profileId === activeProfileId() && row.contentType === "hidden-channel")
          .map((row) => row.contentRef),
      ),
    [hidden],
  );
}
